import { useState, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AddressResult {
  address: string;
  postalCode: string;
}

interface AddressSearchInputProps {
  value: string;
  onAddressSelect: (address: string, postalCode: string) => void;
  placeholder?: string;
}

export function AddressSearchInput({
  value,
  onAddressSelect,
  placeholder = "Search by address or postal code...",
}: AddressSearchInputProps) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState<AddressResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [noResultsDismissed, setNoResultsDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const selectedRef = useRef(false);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  const canSearch = searchQuery !== null && searchQuery.length >= 2;
  const q = trpc.system.searchAddresses.useQuery(
    { input: canSearch ? searchQuery! : "  " },
    {
      enabled: canSearch,
      retry: false,
    }
  );

  useEffect(() => {
    if (selectedRef.current) return;
    if (q.data && !q.isFetching) {
      setResults(q.data);
      setIsOpen(true);
    }
  }, [q.data, q.isFetching]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      setSearchQuery(null);
      setNoResultsDismissed(false);
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (!selectedRef.current) {
        setNoResultsDismissed(false);
        setSearchQuery(query.trim());
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (result: AddressResult) => {
    selectedRef.current = true;
    setQuery(result.address);
    onAddressSelect(result.address, result.postalCode);
    setIsOpen(false);
    setResults([]);
    setSearchQuery(null);
    setTimeout(() => {
      selectedRef.current = false;
    }, 500);
  };

  const handleUseManualAddress = () => {
    const t = query.trim();
    if (t.length < 2) return;
    selectedRef.current = true;
    onAddressSelect(t, "");
    setIsOpen(false);
    setResults([]);
    setSearchQuery(null);
    setTimeout(() => {
      selectedRef.current = false;
    }, 500);
  };

  const handleTrySearchAgain = () => {
    setNoResultsDismissed(false);
    if (query.trim().length >= 2) {
      void q.refetch();
    }
  };

  const searchDone =
    searchQuery !== null &&
    searchQuery.length >= 2 &&
    !q.isFetching &&
    (q.isFetched || q.isError);

  const showNoResultsPanel =
    query.trim().length >= 2 &&
    searchDone &&
    !selectedRef.current &&
    (q.isError || (q.data?.length ?? 0) === 0);

  return (
    <div className="relative">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
        Address or Postal Code <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= 2 && results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="w-full border border-gray-200 px-3 py-2 pl-9 text-sm focus:outline-none focus:border-black transition-colors rounded-none"
        />
        {q.isFetching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-black rounded-full" />
          </div>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 max-h-64 overflow-y-auto"
        >
          {results.map((result, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(result)}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors text-sm"
            >
              <div className="font-medium text-gray-900">{result.address}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{result.postalCode}</div>
            </button>
          ))}
        </div>
      )}

      {showNoResultsPanel && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-amber-200 shadow-lg z-50 px-3 py-3 text-sm space-y-3">
          {!noResultsDismissed && (
            <div className="flex gap-2 text-amber-900">
              <p className="flex-1">
                {q.isError
                  ? "Address search is unavailable right now."
                  : "No addresses found. You can use the entered address instead."}
              </p>
              <button
                type="button"
                className="shrink-0 p-0.5 rounded hover:bg-amber-100"
                aria-label="Dismiss"
                onClick={() => setNoResultsDismissed(true)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="default" className="rounded-none" onClick={handleUseManualAddress}>
              Use this address
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={handleTrySearchAgain}>
              Try again
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Your typed text stays in the field — you can edit it and search again, or continue with manual entry.
          </p>
        </div>
      )}
    </div>
  );
}
